import React from "react"

const VARIANTS = {
  default: {
    id: "advanced-llm-settings",
    title: "Advanced LLM Settings",
    fields: [
      { key: "max_tokens", label: "Max Tokens", type: "number", placeholder: "model default", min: 1, max: 100000 },
      { key: "temperature", label: "Temperature", type: "range", defaultDisplay: "0.2", min: 0, max: 2, step: 0.1 },
      { key: "top_p", label: "Top P", type: "number", placeholder: "e.g. 0.9", min: 0, max: 1, step: 0.05 },
      { key: "top_k", label: "Top K", type: "number", placeholder: "e.g. 40", min: 1, max: 100 },
    ],
  },
  screening: {
    id: "advanced-screening-llm-settings",
    title: "Advanced Screening LLM Settings",
    fields: [
      { key: "screening_max_tokens", label: "Max Tokens", type: "number", placeholder: "model default", min: 1, max: 100000 },
      { key: "screening_temperature", label: "Temperature", type: "range", defaultDisplay: "0", min: 0, max: 2, step: 0.1 },
      { key: "screening_top_p", label: "Top P", type: "number", placeholder: "e.g. 0.9", min: 0, max: 1, step: 0.05 },
      { key: "screening_top_k", label: "Top K", type: "number", placeholder: "e.g. 40", min: 1, max: 100 },
    ],
  },
  grading: {
    id: "advanced-grading-llm-settings",
    title: "Advanced grading settings",
    fields: [
      { key: "grading_max_tokens", label: "Max Tokens", type: "number", placeholder: "model default", min: 1, max: 100000 },
      { key: "grading_temperature", label: "Temperature", type: "range", defaultDisplay: "0.1", min: 0, max: 2, step: 0.1 },
      { key: "grading_top_p", label: "Top P", type: "number", placeholder: "e.g. 0.9", min: 0, max: 1, step: 0.05 },
      { key: "grading_top_k", label: "Top K", type: "number", placeholder: "e.g. 40", min: 1, max: 100 },
    ],
  },
  classify: {
    id: "advanced-classify-llm-settings",
    title: "Advanced classifier settings",
    fields: [
      { key: "classify_max_tokens", label: "Max Tokens", type: "number", placeholder: "model default", min: 1, max: 100000 },
      { key: "classify_temperature", label: "Temperature", type: "range", defaultDisplay: "0", min: 0, max: 2, step: 0.1 },
      { key: "classify_top_p", label: "Top P", type: "number", placeholder: "e.g. 0.9", min: 0, max: 1, step: 0.05 },
      { key: "classify_top_k", label: "Top K", type: "number", placeholder: "e.g. 40", min: 1, max: 100 },
    ],
  },
  extraction: {
    id: "advanced-extraction-llm-settings",
    title: "Advanced extraction settings",
    fields: [
      { key: "extraction_max_tokens", label: "Max Tokens", type: "number", placeholder: "model default", min: 1, max: 100000 },
      { key: "extraction_temperature", label: "Temperature", type: "range", defaultDisplay: "0", min: 0, max: 2, step: 0.1 },
      { key: "extraction_top_p", label: "Top P", type: "number", placeholder: "e.g. 0.9", min: 0, max: 1, step: 0.05 },
      { key: "extraction_top_k", label: "Top K", type: "number", placeholder: "e.g. 40", min: 1, max: 100 },
    ],
  },
  corpus_ingest: {
    id: "advanced-corpus-ingest-llm-settings",
    title: "Advanced ingest LLM settings",
    fields: [
      { key: "corpus_ingest_llm_max_tokens", label: "Max Tokens", type: "number", placeholder: "model default", min: 1, max: 100000 },
      { key: "corpus_ingest_llm_temperature", label: "Temperature", type: "range", defaultDisplay: "0.2", min: 0, max: 2, step: 0.1 },
      { key: "corpus_ingest_llm_top_p", label: "Top P", type: "number", placeholder: "e.g. 0.9", min: 0, max: 1, step: 0.05 },
      { key: "corpus_ingest_llm_top_k", label: "Top K", type: "number", placeholder: "e.g. 40", min: 1, max: 100 },
    ],
  },
  pubmed: {
    id: "advanced-llm-settings-pubmed",
    title: "Advanced LLM Settings (PubMed match)",
    fields: [
      {
        key: "enrich_pubmed_llm_model",
        label: "Model",
        type: "select",
        options: [
          { value: "gpt-4o-mini", label: "OpenAI GPT-4o Mini (fast, cheap)" },
          { value: "gpt-4o", label: "OpenAI GPT-4o (powerful, balanced)" },
          { value: "gemini-2.5-flash", label: "Gemini 2.5 Flash (fast, 1M context)" },
          { value: "claude-sonnet-4-20250514", label: "Anthropic Claude Sonnet 4" },
        ],
        defaultValue: "gpt-4o-mini",
      },
      { key: "enrich_pubmed_llm_max_tokens", label: "Max Tokens", type: "number", placeholder: "e.g. 8192 (uses model default)", min: 1 },
      { key: "enrich_pubmed_llm_temperature", label: "Temperature", type: "range", defaultDisplay: "0.2", min: 0, max: 2, step: 0.1 },
      { key: "enrich_pubmed_llm_top_p", label: "Top P (optional)", type: "number", placeholder: "e.g. 0.9", min: 0, max: 1, step: 0.01 },
      { key: "enrich_pubmed_llm_top_k", label: "Top K (Claude/Gemini only)", type: "number", placeholder: "optional", min: 0 },
    ],
  },
}

/**
 * Collapsible advanced LLM settings (max tokens, temperature, top_p, top_k; optional model select).
 * @param {"default"|"screening"|"grading"|"classify"|"corpus_ingest"|"pubmed"} variant - Which set of field keys and labels to use
 * @param {Object} formValues - Current form values (keyed by field key)
 * @param {function(string, any)} onFieldChange - (key, value) => void
 * @param {string} [className] - Optional extra classes for the details element (e.g. "mt-2")
 */
function AdvancedLlmSettings({ variant, formValues, onFieldChange, className = "" }) {
  const config = VARIANTS[variant] || VARIANTS.default
  const wrapperClass = [
    "py-3 px-4 bg-white dark:bg-gray-900 border border-gray-200 rounded-lg shadow-sm",
    className,
  ]
    .filter(Boolean)
    .join(" ")

  const renderField = (field) => {
    const value = formValues[field.key]
    const inputClass = "w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
    if (field.type === "range") {
      const display = value ?? field.defaultDisplay ?? "0"
      return (
        <div key={field.key} className="space-y-1">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
            {field.label}: {display}
          </label>
          <input
            type="range"
            className="w-full"
            min={field.min}
            max={field.max}
            step={field.step}
            value={value ?? parseFloat(field.defaultDisplay) ?? 0}
            onChange={(e) => onFieldChange(field.key, e.target.value)}
          />
        </div>
      )
    }
    if (field.type === "select") {
      return (
        <div key={field.key} className="space-y-1">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">{field.label}</label>
          <select
            className={inputClass}
            value={value ?? field.defaultValue ?? ""}
            onChange={(e) => onFieldChange(field.key, e.target.value)}
          >
            {field.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      )
    }
    return (
      <div key={field.key} className="space-y-1">
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">{field.label}</label>
        <input
          type="number"
          className={inputClass}
          placeholder={field.placeholder}
          min={field.min}
          max={field.max}
          step={field.step}
          value={value ?? ""}
          onChange={(e) => onFieldChange(field.key, e.target.value)}
        />
      </div>
    )
  }

  const ChevronDown = () => (
    <span className="inline-block transition-transform group-open:rotate-180 flex-shrink-0" aria-hidden="true">
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </span>
  )

  return (
    <div className={wrapperClass}>
      <details id={config.id} className="group">
        <summary className="cursor-pointer flex items-center gap-2 list-none [&::-webkit-details-marker]:hidden">
          <ChevronDown />
          <div className="w-1 h-5 bg-indigo-500 rounded-full" />
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{config.title}</span>
        </summary>
        <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-100">
          {config.fields.map(renderField)}
        </div>
      </details>
    </div>
  )
}

export default AdvancedLlmSettings
export { VARIANTS }
